import React from 'react'
import InputAdornment from '@material-ui/core/InputAdornment'
import { TextFieldProps as MuiTextFieldProps } from '@material-ui/core/TextField'
import { Field, FieldProps } from 'formik'
import clsx from 'clsx'
import withTableWrapper from './withTableWrapper'
import withInlineWrapper from './withInlineWrapper'
import withEntityContext from './withEntityContext'
import { formatCurrency } from 'utils'
import isNaN from 'lodash/isNaN'
import { TextField as StyledTextField } from './styled'

export type TextFieldProps = MuiTextFieldProps & {
  name: string
  unit?: string
  unitPosition?: 'start' | 'end'
  isCurrency?: boolean
  adjustRight?: boolean
  formatHint?: {
    text: string
    regExp: RegExp
  }
}

const getHelperText = (
  value: FieldProps['field']['value'],
  error: FieldProps['meta']['error'],
  touched: FieldProps['meta']['touched'],
  formatHint: TextFieldProps['formatHint']
): string | undefined => {
  if (!touched) {
    return
  }

  if (error !== undefined) {
    return error
  }

  if (formatHint === undefined || value === '') {
    return
  }

  const { text, regExp } = formatHint

  if (regExp.test(value)) {
    return
  }

  return text
}

const TextField: React.FC<TextFieldProps> = React.forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ name, unit, unitPosition = 'start', isCurrency = false, adjustRight, formatHint, InputProps, InputLabelProps, ...others }, ref) => (
    <Field name={name}>
      {({ field: { value, ...otherField }, meta: { error, touched } }: FieldProps) => {
        const helperText = getHelperText(value, error, touched, formatHint)

        return (
          <StyledTextField
            variant='outlined'
            margin='dense'
            error={Boolean(error) && touched}
            helperText={helperText}
            autoComplete='off'
            fullWidth
            value={value ?? ''}
            className={clsx({
              'Mui-warning': helperText !== undefined && error === undefined
            })}
            InputProps={{
              [unitPosition === 'start' ? 'startAdornment' : 'endAdornment']:
                unit !== undefined ? (
                  <InputAdornment position={unitPosition} disableTypography>
                    {!isCurrency ? unit : unit.toUpperCase()}
                  </InputAdornment>
                ) : undefined,
              notched: false,
              ...InputProps
            }}
            inputProps={{ ref: ref }}
            InputLabelProps={{
              shrink: true,
              ...InputLabelProps
            }}
            {...others}
            {...otherField}
          />
        )
      }}
    </Field>
  )
)

TextField.displayName = 'TextField'

const getTextFieldValue = (
  value: string | number,
  { unit, isCurrency, unitPosition, placeholder, type }: TextFieldProps
): string | JSX.Element => {
  if (isCurrency === true && !isNaN(value) && typeof value === 'number') {
    return formatCurrency(value, unit)
  }

  if (typeof value === 'string' && value === '') {
    value = '-'
  } else if (value === undefined || value === null) {
    value = '-'
  }

  const showingPlaceholder = type === 'password' || (value === '-' && placeholder !== undefined)

  let res = showingPlaceholder ? placeholder : value

  if (unit !== undefined) {
    if (unitPosition === 'start') {
      res = `${unit.toUpperCase()} ${res as string}`
    }
    if (unitPosition === 'end') {
      res = `${res as string} ${unit.toUpperCase()}`
    }
  }

  return <div style={showingPlaceholder ? { color: 'grey' } : undefined}>{res}</div>
}

export const TextFieldWithContext = withEntityContext(TextField, getTextFieldValue)

export const InlineTextField = withInlineWrapper(TextField, getTextFieldValue)
export const InlineTextFieldNoPortal = withInlineWrapper(TextField, getTextFieldValue, { disableControlsPortal: false })
export const InlineTextFieldWithShadow = withInlineWrapper(TextField, getTextFieldValue, { showShadow: true })
export const InlineViewOnlyTextField = withInlineWrapper(TextField, getTextFieldValue, { editable: false })

export const TableTextInlineField = withTableWrapper(InlineTextField)
export const TableViewOnlyTextInlineField = withTableWrapper(InlineViewOnlyTextField)

export default TextField
